// jsPDF is dynamically imported to reduce initial bundle size

export interface ReceiptPDFData {
  merchant_name: string;
  store_address?: string;
  date: string;
  total: number;
  subtotal: number;
  tax: number;
  currency: string;
  line_items?: Array<{
    name: string;
    quantity?: number;
    price: number;
  }>;
  item_categories?: string[];
  deductible_items?: string[];
  non_deductible_items?: string[];
  tax_savings_estimate?: number;
  deduction_explanation?: string;
  confidence_score?: number;
  parsed_json?: any;
  imageUrl?: string;
}

export async function generateReceiptPDF(receipt: ReceiptPDFData): Promise<void> {
  // Dynamically import jsPDF to reduce initial bundle size
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPosition = 20;
  
  // Brand color
  const brandGreen: [number, number, number] = [34, 194, 92]; // #22c25c
  
  // Header
  doc.setFillColor(brandGreen[0], brandGreen[1], brandGreen[2]);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.text('TaxGBT Receipt', margin, 20);
  
  yPosition = 40;
  doc.setTextColor(0, 0, 0);
  
  // Merchant info
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(receipt.merchant_name || 'Unknown Merchant', margin, yPosition);
  yPosition += 10;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  if (receipt.store_address) {
    doc.text(receipt.store_address, margin, yPosition);
    yPosition += 7;
  }
  doc.text(`Date: ${new Date(receipt.date).toLocaleDateString()}`, margin, yPosition);
  yPosition += 15;
  
  // Confidence score
  if (receipt.confidence_score !== undefined) {
    const confidenceColor: [number, number, number] = receipt.confidence_score >= 80 ? [34, 197, 94] :
                           receipt.confidence_score >= 60 ? [234, 179, 8] :
                           [239, 68, 68];
    doc.setTextColor(confidenceColor[0], confidenceColor[1], confidenceColor[2]);
    doc.text(`OCR Confidence: ${receipt.confidence_score}%`, margin, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 10;
  }
  
  // Line items
  if (receipt.line_items && receipt.line_items.length > 0) {
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Items:', margin, yPosition);
    yPosition += 7;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    receipt.line_items.forEach((item, index) => {
      const category = receipt.item_categories?.[index] || '';
      const isDeductible = receipt.deductible_items?.includes(item.name);
      
      let itemText = `• ${item.name}`;
      if (item.quantity && item.quantity > 1) {
        itemText += ` (x${item.quantity})`;
      }
      itemText += ` - ${receipt.currency} ${item.price.toFixed(2)}`;
      
      if (category) {
        itemText += ` [${category}]`;
      }
      
      doc.text(itemText, margin + 5, yPosition);
      yPosition += 6;
      
      if (isDeductible) {
        doc.setTextColor(brandGreen[0], brandGreen[1], brandGreen[2]);
        doc.setFontSize(8);
        doc.text('✓ Tax Deductible', margin + 10, yPosition);
        yPosition += 5;
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
      }
      
      // Check if we need a new page
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
    });
    
    yPosition += 10;
  }
  
  // Totals
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Summary:', margin, yPosition);
  yPosition += 7;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Subtotal: ${receipt.currency} ${receipt.subtotal.toFixed(2)}`, margin + 5, yPosition);
  yPosition += 6;
  doc.text(`Tax: ${receipt.currency} ${receipt.tax.toFixed(2)}`, margin + 5, yPosition);
  yPosition += 6;
  doc.setFont(undefined, 'bold');
  doc.text(`Total: ${receipt.currency} ${receipt.total.toFixed(2)}`, margin + 5, yPosition);
  yPosition += 15;
  
  // Tax savings
  if (receipt.tax_savings_estimate && receipt.tax_savings_estimate > 0) {
    doc.setFillColor(brandGreen[0], brandGreen[1], brandGreen[2]);
    doc.setDrawColor(brandGreen[0], brandGreen[1], brandGreen[2]);
    doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, 25, 3, 3, 'S');
    
    doc.setTextColor(brandGreen[0], brandGreen[1], brandGreen[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`Estimated Tax Savings: ${receipt.currency} ${receipt.tax_savings_estimate.toFixed(2)}`, margin + 5, yPosition + 5);
    yPosition += 20;
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');
    doc.text('Based on eligible business deductions', margin + 5, yPosition + 3);
    yPosition += 15;
  }
  
  // Deduction explanation
  if (receipt.deduction_explanation) {
    yPosition += 5;
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Tax Deduction Analysis:', margin, yPosition);
    yPosition += 7;
    
    doc.setFont(undefined, 'normal');
    const splitText = doc.splitTextToSize(receipt.deduction_explanation, pageWidth - 2 * margin);
    doc.text(splitText, margin, yPosition);
    yPosition += splitText.length * 5 + 10;
  }
  
  // Business expense assessment
  if (receipt.parsed_json?.business_expense_assessment) {
    const assessment = receipt.parsed_json.business_expense_assessment;
    
    if (yPosition > 240) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Business Expense Assessment:', margin, yPosition);
    yPosition += 7;
    
    doc.setFont(undefined, 'normal');
    doc.text(`Status: ${assessment.overall_status.toUpperCase()}`, margin + 5, yPosition);
    yPosition += 6;
    
    if (assessment.explanation) {
      const splitExp = doc.splitTextToSize(assessment.explanation, pageWidth - 2 * margin - 10);
      doc.text(splitExp, margin + 5, yPosition);
      yPosition += splitExp.length * 5 + 5;
    }
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 15;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated by TaxGBT on ${new Date().toLocaleString()}`, margin, footerY);
  doc.text('Keep this receipt for your tax records', margin, footerY + 5);
  
  // Save the PDF
  const filename = `receipt-${receipt.merchant_name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'unknown'}-${new Date(receipt.date).toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}
